import { useState, useEffect } from 'react';
import throttle from 'lodash.throttle';

// The breakpoint the server renders with. The server has no viewport, so the
// client's FIRST render must use this same value or hydration mismatches
// (React #418: "the server rendered HTML didn't match the client").
//
// 'xs' is deliberate: it is what SSR already emitted before this was fixed
// (getDeviceConfig(false) hit `false < 640`), so the HTML Sitecore caches and
// crawlers see is unchanged. The real breakpoint is resolved after mount.
export const SSR_BREAKPOINT = 'xs';

// CALLERS: never capture the return value of useBreakpoint() in a useState
// initializer, and never leave it out of the deps of a useMemo/useEffect that
// reads it. An initializer runs exactly once, so it would permanently freeze
// the component at 'xs' and it would render its mobile variant on desktop.
// Derive from the hook's value on every render and keep only user intent
// (clicks, toggles) in state. See RecentlyViewed / PortraitCards for the shape.

const getDeviceConfig = (width) => {
  if (width < 640) {
    return 'xs';
  } else if (width >= 640 && width < 768) {
    return 'sm';
  } else if (width >= 768 && width < 1024) {
    return 'md';
  } else if (width >= 1024 && width < 1280) {
    return 'lg';
  } else if (width >= 1280 && width < 1920) {
    return 'xl';
  } else {
    return '2xl';
  }
};

const useBreakpoint = () => {
  const [brkPnt, setBrkPnt] = useState(SSR_BREAKPOINT);

  useEffect(() => {
    // Runs only in the browser, and only after hydration has committed.
    setBrkPnt(getDeviceConfig(window.innerWidth));

    const calcInnerWidth = throttle(() => {
      setBrkPnt(getDeviceConfig(window.innerWidth));
    }, 200);

    window.addEventListener('resize', calcInnerWidth);
    return () => {
      calcInnerWidth.cancel?.();
      window.removeEventListener('resize', calcInnerWidth);
    };
  }, []);

  return brkPnt;
};
export default useBreakpoint;
